import React, {useEffect} from "react";
import {useQueryFilter} from "../../../components/dataTable/customHooks";
import {usePagination} from "@components/dataTable/usePagination";
import {useClientSideProcessing} from "@components/dataTable/useClientSideProcessing";
import {CreateButton, useEditor} from "../../../core/editor";
import {CCNLLivelloRecord} from "@commons/mapping/SettoriCCNL";
import CCNLLivelliEditor from "./formLivelliCCNL";
import DTTitle from "../../../components/dataTable/TableTitle";
import Pagination from "../../../components/dataTable/Pagination";
import * as Buttons from "../../../components/dataTable/buttons";
import DTTable from "../../../components/dataTable/Table";
import {
    CCNLParams,
    useDeleteLivelloCCNLMutation,
    useGetTabellaLivelliCCNLQuery,
    useInsertLivelloMutation,
    useLazyGetSingoloLivelloCCNLQuery,
    useUpdateLivelloMutation
} from "@commons/features/sistema/ULLivelliCCNL.api";
import {useFactory} from "@commons/utils/useFactory";
import {formatImporto} from "../../lavoratori/Lavoratori.retrib.page";

interface CCNLLivelloPageProps {
    codContratto: string | null,
    codContrattoUNILAV: string | null,
}

const CCNLLivelloPage: React.FC<CCNLLivelloPageProps> = ({codContratto, codContrattoUNILAV}) => {
    const pagination = usePagination('',1, 10);
    const {page, setPage, pageLength, setPageLength, search, setSearch} = pagination;

    const {filter, setFilter, skip} = useQueryFilter<CCNLParams>({codContrattoUNILAV: undefined, codContratto: undefined} as CCNLParams, pagination);
    const {data} = useGetTabellaLivelliCCNLQuery(filter, {skip: skip || !codContrattoUNILAV || !codContratto});
    const localData = useClientSideProcessing<CCNLLivelloRecord, keyof CCNLLivelloRecord>(data, pagination, ['codLivello', 'descrizioneUNILAV']);

    const [getTrigger, result, promiseInfo] = useLazyGetSingoloLivelloCCNLQuery();
    const [insertTrigger, insertResult] = useInsertLivelloMutation();
    const [updateTrigger, updateResult] = useUpdateLivelloMutation();
    const [deleteTrigger, deleteResult] = useDeleteLivelloCCNLMutation();

    const {
        resource,
        create,
        cancel,
        URI,
        loadResource,
        saveResource,
        deleteResource,
        editorState
    } = useEditor<CCNLLivelloRecord>(insertTrigger, getTrigger, updateTrigger, deleteTrigger);

    const defaultValues = useFactory(CCNLLivelloRecord.factory, {codContratto, codContrattoUNILAV}); // N.B. useFactory utilizza useMemo!
    // const defaultValues = {codContratto, codContrattoUNILAV} as CCNLLivelloRecord;

    useEffect(() => {
        if (codContrattoUNILAV && codContratto) {
            setFilter(filter => ({
                ...filter,
                codContrattoUNILAV: codContrattoUNILAV,
                codContratto: codContratto,
            }));
        }
        cancel();
    }, [codContrattoUNILAV, codContratto, setFilter, cancel])

    //const defaultValues = CCNLLivelloRecord.factory({codContratto, codContrattoUNILAV});

    return <>
        <div className="px-2 py-3 bg-blue-200 rounded-lg">
            <div className="relative">
                <div className="absolute right-2">
                    <CreateButton<CCNLLivelloRecord> create={create} label="Livello"/>
                </div>
            </div>

            {resource && <div className="p-3">
                <CCNLLivelliEditor defaultValues={defaultValues} resource={resource} onSubmit={saveResource} onCancel={cancel}></CCNLLivelliEditor>
            </div>}

            {!resource && <div className="p-5"/>}

        </div>


        <DTTitle id="elencoLivelli" title="Elenco livelli" {...{currentPageLength: pageLength, setPageLength, currentSearch: search, setSearch}}/>

        <DTTable<CCNLLivelloRecord> dtSchema={localData}
                                    header={<>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Cod. livello
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Cod. UNILAV
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Descrizione
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            RML
                                        </th>
                                        <th scope="col" className="py-3.5 w-14"/>
                                    </>}

                                    rows={({item, index, isMobile, currentRow, register}) => (
                                        <tr {...register(item.codLivello + '_' + item.codLivelloUNILAV, index, item.URI === URI)}>
                                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                {item.codLivello}
                                            </td>
                                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                {item.codLivelloUNILAV}
                                            </td>
                                            <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6 min-w-[300px]">
                                                {item.descrizioneUNILAV}
                                            </td>
                                            <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                                {formatImporto(item.rml)}
                                            </td>
                                            <td className="align-middle text-sm font-medium">
                                                <Buttons.Visibility visible={isMobile || index === currentRow}>
                                                    <Buttons.Edit onAction={() => loadResource(item.URI)}/>
                                                    <Buttons.Delete onConfirm={() => {
                                                        deleteResource(item.URI!)
                                                    }}/>
                                                </Buttons.Visibility>
                                            </td>
                                        </tr>)
                                    }
        />

        <Pagination dataTables={localData} currentPage={page} setPage={setPage} currentPageLength={pageLength}></Pagination>

    </>
}

export default CCNLLivelloPage;
